<script>
  import { onMount } from 'svelte';
  import { getApiUrl } from '../api';
  import { authFetch } from '../authService';

  let methods = [];
  let isLoading = true;
  let error = '';
  let successMessage = '';
  
  // Single Entry State
  let currentName = '';
  let currentCountry = 'HK';
  let isActive = true;
  let isEditing = false;
  let currentId = null;

  // Bulk Upload State
  let selectedFile = null;
  let isUploading = false;

  onMount(fetchMethods);

  async function fetchMethods() {
    isLoading = true;
    try {
      const response = await authFetch(getApiUrl('/admin/payment-methods')); // Admin list endpoint
      if (!response.ok) throw new Error('Could not load payment methods.');
      methods = await response.json();
    } catch (e) {
      error = e.message;
    } finally {
      isLoading = false;
    }
  }

  function startEdit(item) {
    isEditing = true;
    currentId = item._id;
    currentName = item.name;
    currentCountry = item.countryCode;
    isActive = item.isActive;
    successMessage = '';
    error = '';
  }

  function cancelEdit() {
    isEditing = false;
    currentId = null;
    currentName = '';
    currentCountry = 'HK';
    isActive = true;
  }

  async function handleSubmit() {
    if (!currentName.trim() || !currentCountry.trim()) {
      error = 'Name and Country are required.'; return;
    }
    
    const payload = { 
        name: currentName.trim(), 
        countryCode: currentCountry.trim().toUpperCase(),
        isActive 
    };
    let url = getApiUrl('/admin/payment-methods');
    let method = 'POST';

    if (isEditing) {
      url = getApiUrl(`/admin/payment-methods/${currentId}`);
      method = 'PUT';
    }

    try {
      const response = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
          const res = await response.json();
          throw new Error(res.error || 'Operation failed.');
      }
      
      successMessage = isEditing ? 'Updated successfully.' : 'Added successfully.';
      cancelEdit();
      await fetchMethods();
    } catch (e) { error = e.message; }
  }

  async function handleDelete(id) {
    if (!confirm('Delete this method?')) return;
    try {
      const response = await authFetch(getApiUrl(`/admin/payment-methods/${id}`), { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to delete.');
      await fetchMethods();
    } catch (e) { alert(e.message); }
  }

  function handleFileSelect(e) { selectedFile = e.target.files[0]; }
  async function handleUpload() {
    if (!selectedFile) return;
    isUploading = true;
    const formData = new FormData();
    formData.append('file', selectedFile);
    try {
      const response = await authFetch(getApiUrl('/payment-methods/upload'), { method: 'POST', body: formData });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error);
      successMessage = `Bulk upload successful! ${result.inserted} added.`;
      selectedFile = null;
      document.getElementById('csv-upload').value = '';
      await fetchMethods();
    } catch (e) { error = e.message; } finally { isUploading = false; }
  }
</script>

<div class="maintenance-container">
  <h2>Payment Methods</h2>
  
  <div class="grid-layout">
    <div class="action-column">
        <!-- Single Entry Form -->
        <div class="card form-section">
            <h3>{isEditing ? 'Edit Method' : 'Add New Method'}</h3>
            <form on:submit|preventDefault={handleSubmit}>
                <div class="form-group">
                    <label>Name</label>
                    <input type="text" bind:value={currentName} placeholder="e.g. Apple Pay" required />
                </div>
                <div class="form-group">
                    <label>Country Code</label>
                    <input type="text" bind:value={currentCountry} placeholder="HK" required />
                </div>
                <div class="form-group checkbox-group">
                    <label>
                        <input type="checkbox" bind:checked={isActive}> Active
                    </label>
                </div>
                <div class="form-actions">
                    {#if isEditing}
                    <button type="button" class="secondary" on:click={cancelEdit}>Cancel</button>
                    {/if}
                    <button type="submit" class="primary">{isEditing ? 'Save Changes' : 'Add Method'}</button>
                </div>
            </form>
        </div>

        <!-- Bulk Upload -->
        <div class="card upload-section">
            <h3>Bulk Upload</h3>
            <p class="hint">CSV: <code>name,countryCode,isActive</code></p>
            <input type="file" id="csv-upload" accept=".csv" on:change={handleFileSelect} />
            <button class="upload-btn" on:click={handleUpload} disabled={isUploading || !selectedFile}>
                {isUploading ? 'Uploading...' : 'Upload CSV'}
            </button>
        </div>
        
        {#if successMessage}<p class="success">{successMessage}</p>{/if}
        {#if error}<p class="error">{error}</p>{/if}
    </div>

    <!-- List -->
    <div class="card list-section">
      <h3>All Methods ({methods.length})</h3>
      {#if isLoading}
        <p>Loading...</p>
      {:else if methods.length === 0}
        <p>No methods found.</p>
      {:else}
        <div class="list-wrapper">
            <ul>
            {#each methods as item (item._id)}
                <li>
                    <div class="item-info">
                        <span class="item-name">{item.name}</span>
                        <div class="tags">
                             <span class="item-tag">{item.countryCode || 'Global'}</span>
                             {#if !item.isActive}
                                <span class="tag-inactive">Inactive</span>
                             {/if}
                        </div>
                    </div>
                    <div class="item-actions">
                        <button class="edit-btn" on:click={() => startEdit(item)}>Edit</button>
                        <button class="delete-btn" on:click={() => handleDelete(item._id)}>Delete</button>
                    </div>
                </li>
            {/each}
            </ul>
        </div>
      {/if}
    </div>
  </div>
</div>

<style>
  .maintenance-container { max-width: 1100px; margin: 2rem auto; padding: 0 1rem; }
  .grid-layout { display: grid; grid-template-columns: 1fr; gap: 2rem; }
  @media(min-width: 768px) { .grid-layout { grid-template-columns: 1fr 1.5fr; } }
  .card { background: #fff; padding: 1.5rem; border-radius: 8px; box-shadow: 0 2px 5px rgba(0,0,0,0.05); margin-bottom: 1.5rem; }
  h3 { margin-top: 0; color: #0056b3; }
  .form-group { margin-bottom: 1rem; text-align: left; }
  .checkbox-group { display: flex; align-items: center; }
  .checkbox-group label { font-weight: normal; cursor: pointer; display: flex; align-items: center; gap: 0.5rem; }
  input[type="checkbox"] { width: auto; margin: 0; }
  label { display: block; margin-bottom: 0.5rem; font-weight: 500; }
  input { width: 100%; padding: 0.75rem; border: 1px solid #ccc; border-radius: 4px; box-sizing: border-box; }
  .form-actions { display: flex; justify-content: flex-end; gap: 0.5rem; }
  button { padding: 0.6rem 1.2rem; border: none; border-radius: 4px; cursor: pointer; font-size: 1rem; color: white; }
  .primary, .upload-btn { background-color: #007bff; width: 100%; }
  .secondary { background-color: #6c757d; }
  button:disabled { background-color: #aaa; cursor: not-allowed; }
  .list-wrapper { max-height: 600px; overflow-y: auto; }
  ul { list-style: none; padding: 0; margin: 0; }
  li { display: flex; justify-content: space-between; align-items: center; padding: 0.8rem 0; border-bottom: 1px solid #eee; }
  .item-info { display: flex; flex-direction: column; gap: 0.2rem; }
  .item-name { font-weight: 500; }
  .tags { display: flex; gap: 5px; }
  .item-tag { font-size: 0.85rem; background: #e9ecef; padding: 2px 6px; border-radius: 4px; }
  .tag-inactive { font-size: 0.85rem; background: #ffeeba; color: #856404; padding: 2px 6px; border-radius: 4px; }
  .item-actions { display: flex; gap: 0.5rem; }
  .edit-btn { background-color: #ffc107; color: #333; padding: 4px 10px; font-size: 0.85rem; }
  .delete-btn { background-color: #dc3545; padding: 4px 10px; font-size: 0.85rem; }
  .success { color: #28a745; text-align: center; }
  .error { color: #dc3545; text-align: center; }
  .hint { font-size: 0.9rem; color: #666; }
  code { background: #f0f0f0; padding: 2px 4px; }
</style>